// src/main.ts

import {
  SeedApiResponse,
  TabId,
  RecentSeed,
  ViewState,
} from "./shared/types.js";
import { renderSummaryHtml } from "./frontend/summaryView.js";

import { normalizeSeedInput } from "./shared/normalize.js";
import {
  FloorDomRefs,
  getVisibleFloors,
  renderFloorView,
} from "./frontend/floorView.js";
import { updateSidebars } from "./frontend/sidebars.js";

// ───────────────── DOM references ─────────────────

const seedForm = document.getElementById("seedForm") as HTMLFormElement | null;

const seedEl = document.getElementById("seed") as HTMLInputElement;
const floorMinEl = document.getElementById("floorMin") as HTMLInputElement;
const floorMaxEl = document.getElementById("floorMax") as HTMLInputElement;

const summaryContainer = document.getElementById("summary") as HTMLDivElement;
const runBtn = document.getElementById("runBtn") as HTMLButtonElement;
const loadingEl = document.getElementById("loading") as HTMLDivElement;

const tabSummaryBtn = document.getElementById("tabSummary") as HTMLButtonElement;
const tabFloorsBtn = document.getElementById("tabFloors") as HTMLButtonElement;
const summarySection = document.getElementById("summarySection") as HTMLDivElement;
const floorsSection = document.getElementById("floorsSection") as HTMLDivElement;

const preFloorEl = document.getElementById("preFloor") as HTMLDivElement;
const floorBoxEl = document.getElementById("floorBox") as HTMLDivElement;
const floorTitleEl = document.getElementById("floorTitle") as HTMLSpanElement;
const floorFeelingEl = document.getElementById("floorFeeling") as HTMLSpanElement;
const floorItemsEl = document.getElementById("floorItems") as HTMLPreElement;

const firstFloorBtn = document.getElementById("firstFloorBtn") as HTMLButtonElement;
const prevFloorBtn = document.getElementById("prevFloorBtn") as HTMLButtonElement;
const nextFloorBtn = document.getElementById("nextFloorBtn") as HTMLButtonElement;
const lastFloorBtn = document.getElementById("lastFloorBtn") as HTMLButtonElement;
const currentFloorInput = document.getElementById("currentFloorInput") as HTMLInputElement;
const floorRangeLabel = document.getElementById("floorRangeLabel") as HTMLSpanElement;

const backToTopBtn = document.getElementById("backToTop") as HTMLButtonElement | null;
const upgradeFilterEl = document.getElementById("upgradeFilter") as HTMLInputElement | null;
const clearSeedBtn = document.getElementById("clearSeedBtn") as HTMLButtonElement | null;
const pasteSeedBtn = document.getElementById("pasteSeedBtn") as HTMLButtonElement | null;

const floorDom: FloorDomRefs = {
  preFloorEl,
  floorBoxEl,
  floorTitleEl,
  floorFeelingEl,
  floorItemsEl,
  currentFloorInput,
  floorRangeLabel,
};

// ───────────────── State ─────────────────

let currentTab: TabId = "summary";
let currentData: SeedApiResponse | null = null;
let currentFloorDepth: number | null = null;
let recentSeeds: RecentSeed[] = [];
const STATE_KEY = "shpdSeedViewState";

// ───────────────── Busy / loading ─────────────────

function setBusy(busy: boolean, message: string) {
  if (!loadingEl || !runBtn) return;
  if (busy) {
    loadingEl.textContent = message;
    loadingEl.classList.remove("hidden");
    runBtn.disabled = true;
  } else {
    loadingEl.classList.add("hidden");
    runBtn.disabled = false;
  }
}

// ───────────────── Tabs ─────────────────

function setTab(tab: TabId) {
  currentTab = tab;
  tabSummaryBtn.classList.toggle("active", tab === "summary");
  tabFloorsBtn.classList.toggle("active", tab === "floors");
  summarySection.classList.toggle("hidden", tab !== "summary");
  floorsSection.classList.toggle("hidden", tab !== "floors");
}

// ───────────────── Local Storage / Caching ─────────────────

function loadState(): ViewState | null {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ViewState;
  } catch {
    return null;
  }
}

function saveState() {
  const state: ViewState = {
    lastSeed: seedEl.value.trim() || undefined,
    floorMin: floorMinEl.value ? Number(floorMinEl.value) : undefined,
    floorMax: floorMaxEl.value ? Number(floorMaxEl.value) : undefined,
    currentTab,
    currentFloorDepth: currentFloorDepth ?? undefined,
    recentSeeds,
  };

  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function syncUrl() {
  const url = new URL(window.location.href);
  const seed = seedEl.value.trim();
  const floorMin = floorMinEl.value || "1";
  const floorMax = floorMaxEl.value || "5";

  if (seed) url.searchParams.set("seed", seed);
  else url.searchParams.delete("seed");
  url.searchParams.set("floorMin", floorMin);
  url.searchParams.set("floorMax", floorMax);
  url.searchParams.set("tab", currentTab);
  if (currentFloorDepth != null) url.searchParams.set("floor", String(currentFloorDepth));
  else url.searchParams.delete("floor");

  window.history.replaceState({}, "", url.toString());
}

// ───────────────── Range / Filter helpers ─────────────────

function sanitizeRange() {
  let min = parseInt(floorMinEl.value || "1", 10);
  let max = parseInt(floorMaxEl.value || "5", 10);
  if (Number.isNaN(min)) min = 1;
  if (Number.isNaN(max)) max = 5;
  if (min < 1) min = 1;
  if (max < 1) max = 1;
  if (min > 100) min = 100;
  if (max > 100) max = 100;
  if (min > max) [min, max] = [max, min];
  floorMinEl.value = String(min);
  floorMaxEl.value = String(max);
  return { min, max };
}

function getUpgradeFilterValue(): number | null {
  if (!upgradeFilterEl) return null;
  const n = Number(upgradeFilterEl.value.trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

// ───────────────── Core: run seed ─────────────────

function run(addToRecent = true) {
  const seed = seedEl.value.trim();
  if (!seed) {
    summaryContainer.innerHTML = "<em>Enter a seed (e.g. ABC-DEF-GHI)</em>";
    currentData = null;
    currentFloorDepth = null;
    updateSidebars(null);
    return;
  }

  const { min, max } = sanitizeRange();
  const params = new URLSearchParams({
    seed,
    floorMin: String(min),
    floorMax: String(max),
  });

  setBusy(true, "Running seed…");

  fetch(`/api/seed?${params}`)
    .then((res) => res.json())
    .then((data: SeedApiResponse) => {
      if ((data as any).error) {
        summaryContainer.innerHTML = `<em>${(data as any).error}</em>`;
        updateSidebars(null);
        setBusy(false, "");
        return;
      }

      currentData = data;
      if (data.normalizedSeed) seedEl.value = data.normalizedSeed;

      const minUpgrade = getUpgradeFilterValue();
      summaryContainer.innerHTML = renderSummaryHtml(data.summary, minUpgrade);
      updateSidebars(data.summary);

      currentFloorDepth = renderFloorView(data, data.floorMin, floorDom);

      if (addToRecent) {
        updateRecentSeeds(data.normalizedSeed, data.floorMin, data.floorMax);
        renderRecentSeeds();
      }

      saveState();
      syncUrl();
      setBusy(false, "");
    })
    .catch(() => {
      summaryContainer.innerHTML = "<em>Network or server error.</em>";
      updateSidebars(null);
      setBusy(false, "");
    });
}

// ───────────────── Recent Seeds (cache display) ─────────────────

function updateRecentSeeds(seed: string, floorMin: number, floorMax: number) {
  const key = `${seed}:${floorMin}-${floorMax}`;
  recentSeeds = [
    { seed, floorMin, floorMax },
    ...recentSeeds.filter((rs) => `${rs.seed}:${rs.floorMin}-${rs.floorMax}` !== key),
  ].slice(0, 5);
}

function renderRecentSeeds() {
  const container = document.getElementById("recentSeeds");
  if (!container) return;
  container.innerHTML = "";
  for (const rs of recentSeeds) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.textContent = `${rs.seed} (${rs.floorMin}-${rs.floorMax})`;
    chip.addEventListener("click", () => {
      seedEl.value = rs.seed;
      floorMinEl.value = String(rs.floorMin);
      floorMaxEl.value = String(rs.floorMax);
      run(true);
    });
    container.appendChild(chip);
  }
}

// ───────────────── Startup / State restore ─────────────────

function applyUrlOrStateOnLoad() {
  const url = new URL(window.location.href);
  const urlSeed = url.searchParams.get("seed");
  const urlFloorMin = url.searchParams.get("floorMin");
  const urlFloorMax = url.searchParams.get("floorMax");
  const urlTab = url.searchParams.get("tab");
  const urlFloor = url.searchParams.get("floor");
  const state = loadState();

  seedEl.value = urlSeed || state?.lastSeed || "";
  floorMinEl.value = urlFloorMin || String(state?.floorMin ?? 1);
  floorMaxEl.value = urlFloorMax || String(state?.floorMax ?? 5);
  currentTab = (urlTab === "summary" || urlTab === "floors")
    ? urlTab
    : state?.currentTab || "summary";
  currentFloorDepth = urlFloor
    ? Number(urlFloor)
    : state?.currentFloorDepth ?? null;
  recentSeeds = state?.recentSeeds ?? [];

  setTab(currentTab);
  renderRecentSeeds();

  if (seedEl.value.trim()) run(false);
  else updateSidebars(null);
}

// ───────────────── Events ─────────────────

document.addEventListener("DOMContentLoaded", () => {
  applyUrlOrStateOnLoad();

  seedForm?.addEventListener("submit", (ev) => {
    ev.preventDefault();
    run(true);
  });

  runBtn.addEventListener("click", () => run(true));
  tabSummaryBtn.addEventListener("click", () => {
    setTab("summary");
    saveState();
    syncUrl();
  });
  tabFloorsBtn.addEventListener("click", () => {
    setTab("floors");
    saveState();
    syncUrl();
  });

  firstFloorBtn.addEventListener("click", () => currentData && renderFloorView(currentData, 1, floorDom));
  lastFloorBtn.addEventListener("click", () => currentData && renderFloorView(currentData, 100, floorDom));

  prevFloorBtn.addEventListener("click", () => {
    if (!currentData) return;
    const visible = getVisibleFloors(currentData);
    const idx = visible.findIndex((f) => f.depth === currentFloorDepth);
    const newDepth = idx <= 0 ? visible[0].depth : visible[idx - 1].depth;
    currentFloorDepth = renderFloorView(currentData, newDepth, floorDom);
  });

  nextFloorBtn.addEventListener("click", () => {
    if (!currentData) return;
    const visible = getVisibleFloors(currentData);
    const idx = visible.findIndex((f) => f.depth === currentFloorDepth);
    const newDepth =
      idx < 0 || idx >= visible.length - 1
        ? visible[visible.length - 1].depth
        : visible[idx + 1].depth;
    currentFloorDepth = renderFloorView(currentData, newDepth, floorDom);
  });

  currentFloorInput.addEventListener("change", () => {
    if (!currentData) return;
    const visible = getVisibleFloors(currentData);
    if (!visible.length) return;
    const v = parseInt(currentFloorInput.value || "", 10);
    if (!Number.isNaN(v)) {
      const clamped = Math.min(
        Math.max(v, visible[0].depth),
        visible[visible.length - 1].depth
      );
      currentFloorDepth = renderFloorView(currentData, clamped, floorDom);
      saveState();
      syncUrl();
    }
  });

  floorMinEl.addEventListener("change", () => {
    sanitizeRange();
    saveState();
    syncUrl();
  });
  floorMaxEl.addEventListener("change", () => {
    sanitizeRange();
    saveState();
    syncUrl();
  });

  // Upgrade filter: rerender summary only
  upgradeFilterEl?.addEventListener("input", () => {
    if (!currentData) return;
    summaryContainer.innerHTML = renderSummaryHtml(
      currentData.summary,
      getUpgradeFilterValue()
    );
  });

  // Clear seed
  clearSeedBtn?.addEventListener("click", () => {
    seedEl.value = "";
    currentData = null;
    summaryContainer.innerHTML = "(run a seed to display summary)";
    updateSidebars(null);
    saveState();
    syncUrl();
  });

  // Paste seed + auto-run
  pasteSeedBtn?.addEventListener("click", async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        const normalizedSeed = normalizeSeedInput(text);
        seedEl.value = text.trim();
        run(true);
      }
    } catch {
      alert("Clipboard not available; please paste manually.");
    }
  });
});

// ───────────────── Back-to-top visibility ─────────────────

if (backToTopBtn) {
  const toggleBackToTop = () => {
    backToTopBtn.classList.toggle("hidden", window.scrollY < 300);
  };
  window.addEventListener("scroll", toggleBackToTop);
  toggleBackToTop();
  backToTopBtn.addEventListener("click", () =>
    window.scrollTo({ top: 0, behavior: "smooth" })
  );
}

window.addEventListener("popstate", () => applyUrlOrStateOnLoad());
